import { TreeDataNode } from '@livv/models';
import { Box, Button, SxProps, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { FC } from 'react';
import useAmplitude from '@utils/hooks/useAmplitude';
import { TrackingEventName } from '@utils/types/amplitude/events';

const buttonTypographyProps: SxProps = {
    fontSize: 14,
    fontWeight: 'medium',
    textDecorationLine: 'underline',
};

const buttonProps: SxProps = {
    '&:hover': {
        backgroundColor: 'transparent',
    },
    pl: 0,
    pr: 2,
    py: 1.5,
};

interface CollapseExpandProps {
    setExpanded: (expanded: string[]) => void;
    treeDataNodes: TreeDataNode[];
}

const CollapseExpandButton: FC<CollapseExpandProps> = ({ setExpanded, treeDataNodes }) => {
    const { t } = useTranslation('common');
    const { track } = useAmplitude();
    const { route } = useRouter();

    const flattenTreeNodes = (nodes: TreeDataNode[]): string[] =>
        nodes.flatMap(({ id, children }) => (children ? [id, ...flattenTreeNodes(children)] : id));

    const handleClick = (direction: 'collapse' | 'expand') => {
        if (route.includes('tree-view')) {
            track({
                name: TrackingEventName.THEMATIC_PLAN_COLLAPSE_EXPAND_ALL_BUTTON_CLICKED,
                params: {
                    direction,
                },
            });
        }
        if (direction === 'expand') {
            setExpanded(flattenTreeNodes(treeDataNodes));
        } else {
            setExpanded([]);
        }
    };

    return (
        <Box
            borderTop="1px solid"
            bottom={0}
            display="flex"
            justifyContent="flex-end"
            mt={2}
            pb={0.5}
            position="sticky"
            sx={{ backgroundColor: 'grey.700', borderColor: 'grey.600' }}
            top={0}
            zIndex={1000}
        >
            <Button onClick={() => handleClick('expand')} sx={buttonProps} disableRipple>
                <Typography data-test-id="expandAll" sx={buttonTypographyProps}>
                    {t('expandAll')}
                </Typography>
            </Button>
            <Button onClick={() => handleClick('collapse')} sx={buttonProps} disableRipple>
                <Typography data-test-id="collapseAll" sx={buttonTypographyProps}>
                    {t('collapseAll')}
                </Typography>
            </Button>
        </Box>
    );
};

export default CollapseExpandButton;
